import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import BingoBall from "./ball/BingoBall";
import SparkleBurst from "./effects/SparkleBurst";
import GlowPulse from "./effects/GlowPulse";
import SpecialFX from "./effects/SpecialFX";
import { audioService } from "@/core/audio/audioService";
import { useBingoSettingsStore } from "@/plugins/bingo/state/bingoSettingsStore";
import { specialConfig, isSpecialNumber } from "./effects/specialFxConfig";
import { getPhase2Special } from "@/plugins/bingo/projector/specials/phase2Specials";

/**
 * CurrentNumberHero — the massive centered reveal on the Projector.
 *
 * Two rendering paths:
 *   • STANDARD numbers (Phase 2, Step 1)
 *     Uses the 6-stage cinematic ball reveal (BingoBall). Ball appears →
 *     spins → opens → number revealed (SACRED, never morphs) → magic
 *     trail to the board → cell lights up.
 *
 *   • SPECIAL numbers (unchanged from Phase 1)
 *     Falls through to the legacy giant-number renderer with the layered
 *     signature FX and unique synthesized sound. Untouched until we build
 *     the Phase 2 special sequences (Doubles, Six Seven, 69, Positive
 *     Energy, Bingo Check).
 *
 * Props:
 *   value         — the number to reveal (null when idle)
 *   onRevealDone  — fired once the ball animation completes and the board
 *                    should promote the cell to "lit up".
 */
export default function CurrentNumberHero({ value, onRevealDone }) {
  const specialEffectsEnabled = useBingoSettingsStore(
    (s) => s.settings.specialEffectsEnabled,
  );
  const nicknamesEnabled = useBingoSettingsStore(
    (s) => s.settings.nicknamesEnabled,
  );

  const special = isSpecialNumber(value) ? specialConfig(value) : null;
  // Phase 2 signatures OWN their number — never fall through to the
  // legacy giant-number renderer even if the value also appears in
  // Phase 1 SPECIAL_NUMBERS. The Phase 2 module renders on top of the
  // standard ball reveal.
  const phase2 = getPhase2Special(value);
  const useSpecialPath = !!special && specialEffectsEnabled && !phase2;
  const showNickname = !!special && nicknamesEnabled && !phase2;

  // For the special path, keep the legacy audio behavior.
  useEffect(() => {
    if (value === null || value === undefined) return;
    if (useSpecialPath) {
      special.playSound();
      // Special path lights the board immediately (Phase 1 behavior).
      onRevealDone?.(value);
    }
    // Standard path: BingoBall owns its own audio + timing.
  }, [value, useSpecialPath, special, onRevealDone]);

  // Standard path (Phase 2 ball reveal)
  if (!useSpecialPath) {
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        {value !== null && value !== undefined ? (
          <BingoBall
            key={value}
            value={value}
            onCellLightReady={(v) => onRevealDone?.(v)}
          />
        ) : (
          <IdleDash />
        )}
      </div>
    );
  }

  // Special path — legacy giant number + FX + sound (untouched).
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(700px 400px at 50% 50%, var(--ic-primary-glow), transparent 65%)",
          opacity: value === null ? 0 : 0.9,
          transition: "opacity 400ms ease",
        }}
      />

      <AnimatePresence>
        {value !== null && (
          <div
            key={`fx-${value}`}
            className="absolute inset-0 pointer-events-none"
          >
            <GlowPulse />
            <SparkleBurst count={28} radius={340} />
            <SpecialFX animationKey={special.animationKey} />
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="popLayout">
        {value !== null ? (
          <motion.div
            key={value}
            initial={{ scale: 0.55, opacity: 0, rotate: -6 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 1.2, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 16 }}
            className="ic-font-display font-black leading-none tracking-tighter select-none relative z-10 flex flex-col items-center"
            data-testid="projector-current-number"
            style={{
              color: "#fff",
              textShadow:
                "0 0 60px var(--ic-primary-glow), 0 0 120px var(--ic-primary-glow)",
            }}
          >
            <span style={{ fontSize: "clamp(10rem, 22vw, 24rem)" }}>
              {value}
            </span>
            {showNickname && (
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                data-testid="projector-current-nickname"
                className="mt-4 tracking-[0.4em] uppercase font-medium"
                style={{
                  fontSize: "clamp(1rem, 1.6vw, 2rem)",
                  color: "var(--ic-primary)",
                }}
              >
                {special.nickname}
              </motion.span>
            )}
          </motion.div>
        ) : (
          <IdleDash />
        )}
      </AnimatePresence>
    </div>
  );
}

function IdleDash() {
  return (
    <motion.div
      key="empty"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="ic-font-display font-black text-white/25 select-none"
      style={{ fontSize: "clamp(6rem, 12vw, 14rem)" }}
    >
      —
    </motion.div>
  );
}
